import { useRef, useState } from 'react';

import {
  MessageType,
  MountedMessage,
  OrderShort,
  SdkMessage,
} from '@solidgate/react-sdk';

import { AnalyticPaymentMethods, MerchantData } from '~/types/payment';

import { usePaymentIntentData } from './usePaymentIntentData';
import usePaypalLogic from './usePaypalLogic';
import { PaymentApi } from '../types';

type UsePaymentFormLogicOptions = {
  paymentApi: PaymentApi;
  onPaymentSuccess?: (order: OrderShort) => void | Promise<void>;
  onPaymentFail?: (message: string, method: AnalyticPaymentMethods) => void;
  onPaymentSubmit?: (method: AnalyticPaymentMethods) => void;
  onCreateCardIntentError?: (errorMessage: string) => void;
  onFormReady?: () => void;
};

type PaymentFormLogicReturn = {
  isLoading: boolean;
  paymentError: string;
  merchantData: MerchantData | null;
  paypalBtnRef: React.RefObject<HTMLDivElement | null>;
  appleContainerRef: React.RefObject<HTMLDivElement | null>;
  googleContainerRef: React.RefObject<HTMLDivElement | null>;
  mountedMethods: Record<MountedMethods, boolean>;
  handleCloseError: () => void;
  handlePaymentSuccess: (order: OrderShort) => void | Promise<void>;
  handlePaymentFail: (message: string, method: AnalyticPaymentMethods) => void;
  handlePaymentSubmit: (method: AnalyticPaymentMethods) => void;
  handleOnFormMounted: (event: SdkMessage[MessageType.Mounted]) => void;
};

type MountedMethods = Extract<
  MountedMessage['entity'],
  'googlebtn' | 'applebtn' | 'paypal' | 'form'
>;

export const usePaymentFormLogic = ({
  paymentApi,
  onPaymentSuccess,
  onPaymentFail,
  onPaymentSubmit,
  onCreateCardIntentError,
  onFormReady,
}: UsePaymentFormLogicOptions): PaymentFormLogicReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [mountedMethods, setMountedMethods] = useState<
    Record<MountedMethods, boolean>
  >({
    googlebtn: false,
    applebtn: false,
    paypal: false,
    form: false,
  });

  const appleContainerRef = useRef<HTMLDivElement | null>(null);
  const googleContainerRef = useRef<HTMLDivElement | null>(null);

  const { merchantData, reinitializePaymentIntent } = usePaymentIntentData({
    paymentApi,
    onCreateCardIntentError,
  });

  const handleCloseError = (): void => {
    setPaymentError('');
  };

  const handlePaymentSuccess = async (order: OrderShort): Promise<void> => {
    onPaymentSuccess && (await onPaymentSuccess(order));
    setIsLoading(false);
  };

  const handlePaymentFail = (
    message: string,
    method: AnalyticPaymentMethods,
  ): void => {
    onPaymentFail && onPaymentFail(message, method);

    setIsLoading(false);
    reinitializePaymentIntent();
    setPaymentError(message);
  };

  const handlePaymentSubmit = (method: AnalyticPaymentMethods): void => {
    onPaymentSubmit && onPaymentSubmit(method);
    setIsLoading(true);
  };

  const handleOnFormMounted = (
    event: SdkMessage[MessageType.Mounted],
  ): void => {
    setMountedMethods((prev) => ({
      ...prev,
      [event.entity]: true,
    }));
    onFormReady && setTimeout(() => onFormReady(), 500);
  };

  const { paypalBtnRef } = usePaypalLogic({
    paymentApi,
    onPaymentSuccess: handlePaymentSuccess,
    onPaymentFail: handlePaymentFail,
    onPaymentSubmit: handlePaymentSubmit,
    onMounted: handleOnFormMounted,
  });

  return {
    isLoading,
    paymentError,
    merchantData,
    paypalBtnRef,
    appleContainerRef,
    googleContainerRef,
    mountedMethods,
    handleCloseError,
    handlePaymentSuccess,
    handlePaymentFail,
    handlePaymentSubmit,
    handleOnFormMounted,
  };
};
